import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, fmtCurrency, fmtPct } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, ChevronRight, Users, TrendingUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_IMG = "https://images.unsplash.com/photo-1526948531399-320e7e40f0ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBhZHZpc29yJTIwbWVldGluZ3xlbnwwfHx8fDE3ODc4NTI5Njh8MA&ixlib=rb-4.1.0&q=85";

export default function AdminClients() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });

  const { data: clients, isLoading } = useQuery({
    queryKey: ["admin-clients"],
    queryFn: async () => (await api.get("/admin/clients")).data,
  });

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/admin/clients", payload)).data,
    onSuccess: () => {
      toast.success("Client added");
      qc.invalidateQueries({ queryKey: ["admin-clients"] });
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "" });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const totalAum = (clients || []).reduce((a, c) => a + (c.summary?.current_value || 0), 0);

  return (
    <div className="space-y-8" data-testid="admin-clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="overline mb-1">Advisor Console</p>
          <h1 className="font-serif text-4xl text-[#0f172a]">Client Portfolios</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn" className="rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200">
              <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-serif text-2xl">New Client</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Full name</Label>
                <Input data-testid="client-name-input" value={form.name} onChange={upd("name")} /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input data-testid="client-email-input" type="email" value={form.email} onChange={upd("email")} /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input data-testid="client-phone-input" value={form.phone} onChange={upd("phone")} /></div>
              <div className="space-y-1.5"><Label>Temporary password</Label>
                <Input data-testid="client-password-input" value={form.password} onChange={upd("password")} /></div>
            </div>
            <DialogFooter>
              <Button data-testid="save-client-btn" disabled={create.isPending}
                onClick={() => create.mutate(form)}
                className="rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200">
                {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* summary strip */}
      <div className="grid gap-6 sm:grid-cols-2 fade-up">
        <Card className="p-6 bg-[#15803d] text-white border-0 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/60">Assets Under Management</p>
            <TrendingUp className="w-4 h-4 text-white/70" strokeWidth={1.5} />
          </div>
          <p className="font-sans text-3xl font-bold tabular-nums">{fmtCurrency(totalAum)}</p>
        </Card>
        <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
          <div className="flex items-center justify-between mb-3">
            <p className="overline">Total Clients</p>
            <Users className="w-4 h-4 text-[#15803d]" strokeWidth={1.5} />
          </div>
          <p className="font-sans text-3xl font-bold tabular-nums text-[#0f172a]">{(clients || []).length}</p>
        </Card>
      </div>

      {isLoading && (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
      )}
      {!isLoading && (clients || []).length === 0 && (
        <Card className="overflow-hidden bg-white border-slate-200 fade-up">
          <div className="grid md:grid-cols-2 items-center">
            <div className="p-10">
              <h3 className="font-serif text-2xl text-[#0f172a] mb-2">No clients yet</h3>
              <p className="text-slate-500 mb-5">Add your first client to start managing their investment portfolio.</p>
              <Button onClick={() => setOpen(true)} className="rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200">
                <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add Client
              </Button>
            </div>
            <img src={EMPTY_IMG} alt="" className="h-56 w-full object-cover hidden md:block" />
          </div>
        </Card>
      )}
      {!isLoading && (clients || []).length > 0 && (
        <div className="space-y-3">
          {clients.map((c, i) => {
            const positive = (c.summary?.gain || 0) >= 0;
            const initials = c.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
            return (
              <Card key={c.id} data-testid={`client-card-${i}`} onClick={() => navigate(`/admin/clients/${c.id}`)}
                className="p-5 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_-4px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 transition-transform duration-300 cursor-pointer fade-up"
                style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-center gap-4">
                  <Avatar className="w-11 h-11 border border-slate-200">
                    <AvatarFallback className="bg-[#15803d] text-white text-sm">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate">{c.name}</p>
                    <p className="text-sm text-slate-400 truncate">{c.email}</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="font-semibold tabular-nums text-[#0f172a]">{fmtCurrency(c.summary?.current_value)}</p>
                    <p className={`text-sm tabular-nums ${positive ? "text-[#059669]" : "text-[#dc2626]"}`}>{fmtPct(c.summary?.gain_pct)}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300" strokeWidth={1.5} />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
